import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useProject,
    useProjectGallery,
    useUploadProjectPhoto,
    useUpdateProjectPhoto,
    useDeleteProjectPhoto,
} from "../../api/projects";

const ProjectGallery: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const projectId = params.projectId ? Number(params.projectId) : null;

    const { data: projectResponse } = useProject(projectId);
    const {
        data: galleryResponse,
        isLoading,
        refetch: refetchGallery,
    } = useProjectGallery(projectId);

    const uploadPhotoMutation = useUploadProjectPhoto();
    const updatePhotoMutation = useUpdateProjectPhoto();
    const deletePhotoMutation = useDeleteProjectPhoto();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleUpload = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!projectId || !selectedFile) {
            setUploadError("Пожалуйста, выберите изображение.");
            return;
        }
        try {
            await uploadPhotoMutation.mutateAsync({
                projectId,
                file: selectedFile,
            });
            setSelectedFile(null);
            setUploadError(null);
            refetchGallery();
        } catch (err: any) {
            setUploadError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось загрузить изображение."
            );
        }
    };

    const toggleActive = async (photoId: number, isActive: boolean) => {
        if (!projectId) {
            return;
        }
        try {
            await updatePhotoMutation.mutateAsync({
                projectId,
                photoId,
                isActive: !isActive,
            });
            refetchGallery();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить статус изображения."
            );
        }
    };

    const updateSortOrder = async (photoId: number, sortOrder: number) => {
        if (!projectId) {
            return;
        }
        try {
            await updatePhotoMutation.mutateAsync({
                projectId,
                photoId,
                sortOrder,
            });
            refetchGallery();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить порядок сортировки."
            );
        }
    };

    const deletePhoto = async (photoId: number) => {
        if (!projectId) {
            return;
        }
        if (window.confirm("Удалить выбранное изображение?")) {
            try {
                await deletePhotoMutation.mutateAsync({ projectId, photoId });
                refetchGallery();
            } catch (err: any) {
                window.alert(
                    err?.response?.data?.message ||
                        err?.message ||
                        "Не удалось удалить изображение."
                );
            }
        }
    };

    if (!projectId) {
        return (
            <div className="p-6 text-red-500">Некорректный идентификатор проекта.</div>
        );
    }

    const project = projectResponse?.data;
    const photos = galleryResponse?.data ?? [];

    return (
        <div className="p-6 space-y-6">
            <div>
                <button
                    onClick={() => navigate(-1)}
                    className="mb-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition"
                >
                    ← Назад
                </button>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Галерея проекта {project?.name}
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Управляйте изображениями и скриншотами, которые будут отображаться на
                    клиентской стороне.
                </p>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Добавить изображение
                </h2>
                <form
                    onSubmit={handleUpload}
                    className="flex flex-col md:flex-row md:items-center gap-4"
                >
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                        className="w-full md:w-auto"
                    />
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        Загрузить
                    </button>
                </form>
                {uploadError && (
                    <div className="mt-3 text-sm text-red-500">{uploadError}</div>
                )}
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Список изображений
                </h2>
                {isLoading ? (
                    <div className="text-gray-500 dark:text-gray-300">
                        Загрузка галереи...
                    </div>
                ) : photos.length ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {photos.map((photo) => (
                            <div
                                key={photo.id}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm"
                            >
                                {photo.url ? (
                                    <img
                                        src={photo.url}
                                        alt={`Фото ${photo.id}`}
                                        className="w-full h-48 object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500">
                                        Нет превью
                                    </div>
                                )}
                                <div className="p-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold">ID: {photo.id}</span>
                                        <span
                                            className={`px-2 py-1 text-xs rounded-full ${
                                                photo.is_active
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-gray-200 text-gray-600"
                                            }`}
                                        >
                                            {photo.is_active ? "Активно" : "Скрыто"}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <label className="text-xs uppercase text-gray-500">
                                            Порядок:
                                        </label>
                                        <input
                                            type="number"
                                            defaultValue={photo.sort_order}
                                            onBlur={(e) =>
                                                updateSortOrder(
                                                    photo.id,
                                                    Number(e.target.value)
                                                )
                                            }
                                            className="w-20 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={() => toggleActive(photo.id, photo.is_active)}
                                            className="px-3 py-1 text-xs rounded bg-indigo-500 text-white hover:bg-indigo-600 transition"
                                        >
                                            {photo.is_active ? "Скрыть" : "Показать"}
                                        </button>
                                        <button
                                            onClick={() => deletePhoto(photo.id)}
                                            className="px-3 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600 transition"
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-gray-500 dark:text-gray-300">
                        Галерея пока пуста. Добавьте первое изображение.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectGallery;

